<script setup>
import { Link, usePage } from '@inertiajs/vue3'
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTweaks } from '@/composables/useTweaks'
import { usePageHeader } from '@/composables/usePageHeader.js'
import { useBreakpoint } from '@/composables/useBreakpoint'
import { stickyBarMounted } from '@/composables/useStickyBar'
import { useInboxStore } from '@/stores/useInboxStore'
import Icon from '@/Components/UI/Icon.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import BellMenu from '@/Components/UI/BellMenu.vue'

const { t } = useI18n()
const page = usePage()
const auth = computed(() => page.props.auth)
const flash = computed(() => page.props.flash || {})
const appSetting = computed(() => page.props.app_setting || {})
const brandName = computed(() => appSetting.value.business_name?.trim() || null)
const brandLogo = computed(() => appSetting.value.logo_url || null)
const isAdmin = computed(() => auth.value?.user?.role === 'admin')

const { tweaks, toggleMode, cycleSidebar } = useTweaks()
const { pageCount, pageCountLabel } = usePageHeader()

const inboxStore = useInboxStore()

// Seed unreadCount from inertia_share on mount and on every Inertia visit.
watch(() => page.props.inbox_unread_count, (n) => {
  if (typeof n === 'number') inboxStore.unreadCount = n
}, { immediate: true })

const { isMobile, isDesktop } = useBreakpoint()
const mobileOpen = ref(false)
const canCreateRental = computed(() => auth.value?.user?.role !== 'viewer')

const fabHrefMap = {
  '/':          '/rentals/new',
  '/rentals':   '/rentals/new',
  '/customers': '/customers/new',
  '/vehicles':  '/vehicles/new',
  '/losses':    '/losses/new',
}
const fabHref = computed(() => {
  const raw = (page.url || '').split('?')[0].split('#')[0]
  const url = raw.length > 1 ? raw.replace(/\/$/, '') : raw
  if (fabHrefMap[url]) return fabHrefMap[url]
  return null
})

const showNotice = ref(false)
const showAlert = ref(false)
let noticeTimer = null
let alertTimer = null

watch(() => flash.value.notice, (val) => {
  if (val) { showNotice.value = true; clearTimeout(noticeTimer); noticeTimer = setTimeout(() => { showNotice.value = false }, 4000) }
}, { immediate: true })

watch(() => flash.value.alert, (val) => {
  if (val) { showAlert.value = true; clearTimeout(alertTimer); alertTimer = setTimeout(() => { showAlert.value = false }, 6000) }
}, { immediate: true })

const onKeydown = (e) => { if (e.key === 'Escape' && mobileOpen.value) mobileOpen.value = false }
onMounted(() => {
  window.addEventListener('keydown', onKeydown)
  inboxStore.connect()
})
onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown)
  inboxStore.disconnect()
})

const navItems = [
  { label: 'nav.dashboard', href: '/',          icon: 'home',     section: 'dashboard'  },
  { label: 'nav.rentals',   href: '/rentals',   icon: 'rental',   section: 'rentals'    },
  { label: 'nav.calendar',  href: '/calendar',  icon: 'calendar', section: 'calendar'   },
  { label: 'nav.customers', href: '/customers', icon: 'users',    section: 'customers'  },
  { label: 'nav.fleet',     href: '/vehicles',  icon: 'fleet',    section: 'fleet'      },
  { label: 'nav.fleetMap',  href: '/gps/fleet', icon: 'map',      section: 'fleet_map'  },
  { label: 'nav.suppliers',    href: '/vehicle_sources', icon: 'truck',   section: 'suppliers'   },
  { label: 'nav.tour_vendors', href: '/tour_vendors',    icon: 'compass', section: 'tour_vendors' },
  { label: 'nav.drivers',      href: '/drivers',         icon: 'users',   section: 'operations'  },
  { label: 'nav.reports',   href: '/reports/vehicles', icon: 'bolt', section: 'reports'  },
  { label: 'nav.losses',    href: '/losses',    icon: 'alert',    section: 'losses'     },
  { label: 'nav.inbox',     href: '/inbox',     icon: 'chat',     section: 'inbox'      },
]

const adminItems = [
  { label: 'nav.approvals',      href: '/admin/approvals',      icon: 'check' },
  { label: 'nav.tour_approvals', href: '/admin/tour_approvals', icon: 'check' },
  { label: 'nav.settings',       href: '/settings',             icon: 'settings' },
]

const permissions = computed(() => auth.value?.user?.permissions ?? {})
const visibleNavItems = computed(() =>
  isAdmin.value ? navItems : navItems.filter(i => permissions.value[i.section] !== false)
)

const allNavHrefs = computed(() => [
  ...navItems.map(i => i.href),
  ...adminItems.map(i => i.href),
])

const activeHref = computed(() => {
  const url = page.url
  if (url === '/') return '/'
  if (url.startsWith('/users') || url.startsWith('/settings')) return '/settings'
  const matches = allNavHrefs.value.filter(h => h !== '/' && (url === h || url.startsWith(h + '/')))
  if (!matches.length) return null
  return matches.reduce((a, b) => (b.length > a.length ? b : a))
})

const isActive = (href) => href === activeHref.value

const rail = computed(() => tweaks.sidebar === 'rail' && !isMobile.value)
const hidden = computed(() => tweaks.sidebar === 'hidden' && !isMobile.value)
const sidebarWidth = computed(() => hidden.value ? 0 : (rail.value ? 72 : 236))

const pageTitle = computed(() => {
  const url = page.url
  if (url === '/') return t('nav.dashboard')
  if (url.startsWith('/inbox')) return t('nav.inbox')
  if (url.startsWith('/calendar')) return t('nav.calendar')
  if (url.startsWith('/rentals')) return t('nav.rentals')
  if (url.startsWith('/customers')) return t('nav.customers')
  if (url.startsWith('/vehicles')) return t('nav.fleet')
  if (url.startsWith('/gps')) return t('nav.fleetMap')
  if (url.startsWith('/vehicle_sources')) return t('nav.suppliers')
  if (url.startsWith('/tour_vendors')) return t('nav.tour_vendors')
  if (url.startsWith('/drivers')) return t('nav.drivers')
  if (url.startsWith('/reports')) return t('nav.reports')
  if (url.startsWith('/losses')) return t('nav.losses')
  if (url.startsWith('/users') || url.startsWith('/settings')) return t('nav.settings')
  return ''
})

const initials = computed(() =>
  (auth.value?.user?.full_name || '?')
    .split(' ').map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
)

const inboxUnread = computed(() => inboxStore.unreadCount || 0)
const isDev = computed(() => !!page.props.dev)

const sidebarIcon = computed(() => ({
  full: 'sidebar-full', rail: 'sidebar-rail', hidden: 'sidebar-hidden',
}[tweaks.sidebar] || 'sidebar-full'))
</script>

<template>
  <div
    class="flex min-h-screen"
    style="background: var(--bg);"
  >
    <!-- Sidebar (desktop) -->
    <aside
      v-if="!hidden && !isMobile"
      class="flex-shrink-0 flex flex-col self-start sticky top-0"
      :style="{
        width: sidebarWidth + 'px',
        height: '100vh',
        background: 'var(--surface)',
        borderRight: '1px solid var(--line)',
        padding: rail ? '16px 10px' : '18px 16px',
        transition: 'width 180ms ease',
        overflowY: 'auto',
      }"
    >
      <!-- Brand -->
      <Link
        href="/"
        class="flex items-center gap-3 pb-5 -mt-1"
        :class="rail ? 'justify-center' : ''"
      >
        <img
          v-if="brandLogo"
          :src="brandLogo"
          alt=""
          :style="{ width: '44px', height: '44px', objectFit: 'contain', borderRadius: 'var(--radius-md)' }"
        >
        <div
          v-else
          class="brand-mark"
          aria-hidden="true"
          :style="{ width: '44px', height: '44px', fontSize: '20px' }"
        >
          {{ brandName ? brandName.charAt(0).toLowerCase() : 's' }}
        </div>
        <div
          v-if="!rail"
          class="leading-[1.1] min-w-0"
        >
          <div
            class="font-display truncate"
            style="font-size: 1rem; color: var(--ink); font-weight: 500;"
          >
            <template v-if="brandName">
              {{ brandName }}
            </template>
            <template v-else>
              Rental CRM
            </template>
          </div>
        </div>
      </Link>

      <!-- Nav -->
      <nav
        class="flex-1 flex flex-col gap-0.5"
        aria-label="Primary navigation"
      >
        <Link
          v-for="item in visibleNavItems"
          :key="item.href"
          :href="item.href"
          class="nav-item"
          :class="{ active: isActive(item.href), rail, 'mt-auto': item.section === 'inbox' }"
          :title="rail ? t(item.label) : undefined"
          :aria-current="isActive(item.href) ? 'page' : undefined"
        >
          <Icon
            :name="item.icon"
            :size="18"
          />
          <span
            v-if="rail && item.section === 'inbox' && inboxUnread > 0"
            class="absolute top-1.5 right-1.5 rounded-full"
            :style="{ width: '8px', height: '8px', background: 'var(--accent)' }"
          />
          <span
            v-if="!rail"
            class="flex-1 text-left"
          >{{ t(item.label) }}</span>
          <span
            v-if="!rail && item.section === 'inbox' && inboxUnread > 0"
            class="ml-auto inline-flex items-center justify-center text-[10px] fw-value rounded-full px-1.5 py-0.5"
            :style="{ background: 'var(--accent)', color: 'var(--accentInk)', minWidth: '18px', height: '18px' }"
          >
            {{ inboxUnread > 99 ? '99+' : inboxUnread }}
          </span>
          <span
            v-if="!rail && isActive(item.href)"
            class="active-dot"
          />
        </Link>

        <div
          v-if="isAdmin"
          class="pt-4"
        >
          <p
            v-if="!rail"
            class="mono text-[9.5px] uppercase tracking-[0.12em] px-3 mb-1"
            style="color: var(--muted);"
          >
            Admin
          </p>
          <Link
            v-for="item in adminItems"
            :key="item.href"
            :href="item.href"
            class="nav-item"
            :class="{ active: isActive(item.href), rail }"
            :title="rail ? t(item.label) : undefined"
          >
            <Icon
              :name="item.icon"
              :size="18"
            />
            <span v-if="!rail">{{ t(item.label) }}</span>
          </Link>
        </div>
      </nav>

      <!-- User footer -->
      <div
        class="flex items-center gap-2.5 pt-3"
        style="border-top: 1px solid var(--line);"
        :class="rail ? 'justify-center' : ''"
      >
        <div
          class="user-avatar"
          :style="{ width: (rail ? 30 : 34) + 'px', height: (rail ? 30 : 34) + 'px' }"
        >
          {{ initials }}
        </div>
        <template v-if="!rail">
          <div class="flex-1 min-w-0 leading-[1.2]">
            <div
              class="font-medium truncate"
              style="color: var(--ink); font-size: 0.8125rem;"
            >
              {{ auth?.user?.full_name }}
            </div>
            <div
              class="mono uppercase tracking-[0.06em]"
              style="color: var(--muted); font-size: 0.625rem;"
            >
              {{ auth?.user?.role }}
            </div>
          </div>
          <Link
            href="/users/sign_out"
            method="delete"
            as="button"
            class="p-1 rounded-md"
            style="color: var(--muted); background: transparent; border: 0; cursor: pointer;"
            :title="t('nav.signOut')"
            aria-label="Sign out"
          >
            <Icon
              name="logout"
              :size="16"
            />
          </Link>
        </template>
      </div>
    </aside>

    <!-- Mobile drawer -->
    <template v-if="isMobile">
      <Transition name="overlay">
        <div
          v-if="mobileOpen"
          class="fixed inset-0 z-40"
          style="background: rgba(0,0,0,0.3);"
          @click="mobileOpen = false"
        />
      </Transition>
      <aside
        class="fixed top-0 left-0 bottom-0 z-50 flex flex-col"
        :style="{
          width: '260px',
          transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 220ms ease',
          background: 'var(--surface)',
          borderRight: '1px solid var(--line)',
          padding: '18px 16px',
          paddingBottom: 'max(env(safe-area-inset-bottom), 18px)',
        }"
      >
        <Link
          href="/"
          class="flex items-center gap-3 pb-5 -mt-1"
          @click="mobileOpen = false"
        >
          <img
            v-if="brandLogo"
            :src="brandLogo"
            alt=""
            :style="{ width: '44px', height: '44px', objectFit: 'contain', borderRadius: 'var(--radius-md)' }"
          >
          <div
            v-else
            class="brand-mark"
            aria-hidden="true"
            :style="{ width: '44px', height: '44px', fontSize: '20px' }"
          >
            {{ brandName ? brandName.charAt(0).toLowerCase() : 's' }}
          </div>
          <div class="leading-[1.1]">
            <div
              class="font-display"
              style="font-size: 1rem; color: var(--ink); font-weight: 500;"
            >
              <template v-if="brandName">
                {{ brandName }}
              </template>
              <template v-else>
                Rental CRM
              </template>
            </div>
          </div>
        </Link>
        <nav class="flex-1 flex flex-col gap-0.5">
          <Link
            v-for="item in visibleNavItems"
            :key="item.href"
            :href="item.href"
            class="nav-item"
            :class="{ active: isActive(item.href), 'mt-auto': item.section === 'inbox' }"
            @click="mobileOpen = false"
          >
            <Icon
              :name="item.icon"
              :size="18"
            />
            <span class="flex-1 text-left">{{ t(item.label) }}</span>
            <span
              v-if="item.section === 'inbox' && inboxUnread > 0"
              class="ml-auto inline-flex items-center justify-center text-[10px] fw-value rounded-full px-1.5 py-0.5"
              :style="{ background: 'var(--accent)', color: 'var(--accentInk)', minWidth: '18px', height: '18px' }"
            >
              {{ inboxUnread > 99 ? '99+' : inboxUnread }}
            </span>
          </Link>
          <template v-if="isAdmin">
            <p
              class="mono text-[9.5px] uppercase tracking-[0.12em] px-3 mt-4 mb-1"
              style="color: var(--muted);"
            >
              Admin
            </p>
            <Link
              v-for="item in adminItems"
              :key="item.href"
              :href="item.href"
              class="nav-item"
              :class="{ active: isActive(item.href) }"
              @click="mobileOpen = false"
            >
              <Icon
                :name="item.icon"
                :size="18"
              />
              <span>{{ t(item.label) }}</span>
            </Link>
          </template>
        </nav>
      </aside>
    </template>

    <!-- Main column -->
    <div class="flex-1 flex flex-col min-w-0">
      <!-- Topbar -->
      <header class="topbar">
        <button
          v-if="isMobile"
          class="topbar-btn"
          :title="t('nav.menu') || 'Menu'"
          @click="mobileOpen = true"
        >
          <Icon
            name="menu"
            :size="20"
          />
        </button>
        <button
          v-else
          class="topbar-btn"
          :title="`Sidebar: ${tweaks.sidebar}`"
          aria-label="Toggle sidebar"
          @click="cycleSidebar"
        >
          <Icon
            :name="sidebarIcon"
            :size="16"
          />
        </button>

        <h1
          class="font-display m-0 flex items-baseline gap-2 text-[1.125rem] md:text-[1.375rem]"
          style="color: var(--ink); letter-spacing: -0.01em; font-weight: 400;"
        >
          <span>{{ pageTitle }}</span>
          <span
            v-if="pageCount !== null"
            class="mono tabular-nums"
            style="font-size: 0.75rem; color: var(--muted); font-weight: 400;"
          >{{ pageCount }}<span v-if="pageCountLabel">&nbsp;{{ pageCountLabel }}</span></span>
        </h1>

        <div class="flex-1" />

        <Link
          v-if="inboxUnread > 0"
          href="/inbox"
          class="inline-flex items-center justify-center text-[11px] fw-value rounded-full px-2 py-0.5"
          :style="{ background: 'var(--accent)', color: 'var(--accentInk)' }"
        >
          {{ t('inbox.unreadBadge', { n: inboxUnread }) }}
        </Link>
        <BellMenu />
        <a
          v-if="isDev"
          href="/letter_opener"
          target="_blank"
          rel="noopener"
          class="topbar-btn"
          title="Sent emails (dev)"
          aria-label="Sent emails (dev)"
        >
          <Icon
            name="mail"
            :size="16"
          />
        </a>
        <button
          class="topbar-btn"
          :title="tweaks.mode === 'light' ? 'Switch to dark' : 'Switch to light'"
          @click="toggleMode"
        >
          <Icon
            :name="tweaks.mode === 'light' ? 'moon' : 'sun'"
            :size="16"
          />
        </button>

        <Link
          v-if="canCreateRental && isDesktop"
          href="/rentals/new"
        >
          <UiButton
            variant="primary"
            icon-left="plus"
          >
            {{ t('rentals.new') }}
          </UiButton>
        </Link>
        <Link
          v-else-if="canCreateRental && !isMobile"
          href="/rentals/new"
          class="topbar-btn icon-only"
          :title="t('rentals.new')"
          :aria-label="t('rentals.new')"
        >
          <Icon
            name="plus"
            :size="18"
          />
        </Link>
      </header>

      <!-- Content -->
      <main
        class="flex-1 min-w-0 animate-fade w-full mx-auto px-4 pb-24 pt-4 md:px-7 md:pb-10 md:pt-6"
      >
        <slot />
      </main>
    </div>

  </div>

  <!-- Mobile FAB (teleported to body so it's never inside a stacking/overflow parent) -->
  <Teleport to="body">
    <Link
      v-if="isMobile && fabHref && !stickyBarMounted"
      :href="fabHref"
      class="fab icon-only"
      :aria-label="t('rentals.new')"
    >
      <Icon
        name="plus"
        :size="22"
        :stroke="2"
      />
    </Link>
  </Teleport>

  <!-- Flash overlay -->
  <div class="fixed top-4 right-4 z-[80] flex flex-col gap-2 w-80 pointer-events-none">
    <Transition name="flash">
      <div
        v-if="showNotice"
        class="flash-card"
        style="background: var(--okSoft); border-color: color-mix(in oklab, var(--ok) 35%, transparent); color: var(--ok);"
        role="status"
        aria-live="polite"
      >
        <Icon
          name="check"
          :size="16"
        />
        <span class="flex-1">{{ flash.notice }}</span>
        <button
          class="flash-close"
          aria-label="Dismiss"
          @click="showNotice = false"
        >
          ×
        </button>
      </div>
    </Transition>
    <Transition name="flash">
      <div
        v-if="showAlert"
        class="flash-card"
        style="background: var(--dangerSoft); border-color: color-mix(in oklab, var(--danger) 35%, transparent); color: var(--danger);"
        role="alert"
        aria-live="assertive"
      >
        <Icon
          name="alert"
          :size="16"
        />
        <span class="flex-1">{{ flash.alert }}</span>
        <button
          class="flash-close"
          aria-label="Dismiss"
          @click="showAlert = false"
        >
          ×
        </button>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.brand-mark {
  width: 32px; height: 32px; border-radius: 10px;
  background: linear-gradient(135deg, var(--accent), var(--second));
  display: flex; align-items: center; justify-content: center;
  color: white; font-family: var(--display); font-size: 1.125rem; font-weight: 500;
  flex-shrink: 0;
}
.nav-item {
  display: flex; align-items: center; gap: 12px;
  padding: 9px 12px;
  border-radius: 11px;
  color: var(--inkSoft);
  font-family: var(--body);
  font-size: 0.875rem; font-weight: 400;
  transition: background 120ms, color 120ms;
  position: relative;
}
.nav-item.rail { padding: 10px; justify-content: center; }
.nav-item:hover { background: var(--surfaceAlt); }
.nav-item.active {
  background: var(--accentSoft);
  color: var(--accent);
  font-weight: 500;
}
.active-dot {
  margin-left: auto; width: 4px; height: 4px; border-radius: 999px;
  background: var(--accent);
}
.user-avatar {
  border-radius: 999px;
  background: var(--accentSoft); color: var(--accent);
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 0.75rem; font-weight: 600; flex-shrink: 0;
}

.topbar {
  min-height: 56px; flex-shrink: 0;
  border-bottom: 1px solid var(--line);
  background: color-mix(in oklab, var(--bg) 82%, transparent);
  backdrop-filter: blur(8px);
  display: flex; align-items: center; gap: 12px; padding: 0 28px 0 16px;
  position: sticky; top: 0; z-index: 30;
}
.topbar-btn {
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink);
  border-radius: 10px;
  height: 36px;
  min-width: 36px;
  padding: 0 10px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 120ms, border-color 120ms;
}
.topbar-btn:hover { border-color: var(--lineStrong); }
.topbar-btn.icon-only,
.topbar-btn:not(:has(> :nth-child(2))) { padding: 0; width: 36px; }

.sidebar-toggle {
  border: 0;
  background: transparent;
  color: var(--muted);
  border-radius: 8px;
  padding: 6px;
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  transition: background 120ms, color 120ms;
}
.sidebar-toggle:hover { background: var(--surfaceAlt); color: var(--ink); }

.btn-soft {
  background: var(--surfaceAlt);
  color: var(--ink);
  padding: 8px 14px;
  border-radius: 12px;
  font-size: 0.8125rem; font-weight: 500;
  border: 0;
  cursor: pointer;
  transition: background 120ms;
}
.btn-soft:hover { background: var(--line); }

/* .fab is defined globally in application.css — scoped styles can miss
   third-party component roots like Inertia's <Link>. */

.flash-card {
  display: flex; align-items: center; gap: 8px;
  padding: 12px; border-radius: 12px;
  border: 1px solid;
  font-size: 0.8125rem;
  pointer-events: auto;
  box-shadow: 0 10px 24px rgba(0,0,0,0.08);
}
.flash-close {
  border: 0; background: transparent; cursor: pointer;
  color: currentColor; opacity: 0.7; line-height: 1; font-size: 1.125rem;
}

.flash-enter-active, .flash-leave-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.flash-enter-from, .flash-leave-to { opacity: 0; transform: translateY(-6px); }

.overlay-enter-active, .overlay-leave-active { transition: opacity 160ms ease; }
.overlay-enter-from, .overlay-leave-to { opacity: 0; }
</style>
